<template>
  <div id="app">
    <admin-login v-if="!loggedIn" @success="onLoginSuccess" />
    <el-container v-else>
      <el-header class="header">
        <span>{{ title }}</span>
        <el-tag v-if="appEnv === 'local'" size="mini" type="info" class="env-tag">本地环境</el-tag>
        <el-button class="logout-btn" type="text" @click="onLogout">退出</el-button>
      </el-header>
      <el-container>
        <el-aside width="200px" class="aside">
          <el-menu :default-active="active" @select="active = $event">
            <el-menu-item index="dashboard">数据概览</el-menu-item>
            <el-menu-item index="tasks">任务监控</el-menu-item>
            <el-menu-item index="users">用户管理</el-menu-item>
            <el-menu-item index="invites">邀请码</el-menu-item>
            <el-menu-item index="quota">会员配额</el-menu-item>
            <el-menu-item index="config">系统配置</el-menu-item>
            <el-menu-item index="highlight">精彩品类调试</el-menu-item>
            <el-menu-item index="security">安全审计</el-menu-item>
            <el-menu-item index="feedback">
              <span>用户反馈</span>
              <span v-if="feedbackUnreadCount > 0" class="nav-badge" />
            </el-menu-item>
            <el-menu-item index="logs">操作日志</el-menu-item>
          </el-menu>
        </el-aside>
        <el-main class="main">
          <dashboard v-if="active === 'dashboard'" />
          <task-list v-else-if="active === 'tasks'" />
          <user-list v-else-if="active === 'users'" />
          <invite-code-list v-else-if="active === 'invites'" />
          <quota-plans v-else-if="active === 'quota'" />
          <system-config v-else-if="active === 'config'" />
          <highlight-tuning v-else-if="active === 'highlight'" />
          <security-audit v-else-if="active === 'security'" />
          <feedback-list
            v-else-if="active === 'feedback'"
            @unread-change="onFeedbackUnreadChange"
          />
          <operation-log v-else />
        </el-main>
      </el-container>
    </el-container>
  </div>
</template>

<script>
import AdminLogin from './views/AdminLogin.vue';
import Dashboard from './views/Dashboard.vue';
import UserList from './views/UserList.vue';
import InviteCodeList from './views/InviteCodeList.vue';
import QuotaPlans from './views/QuotaPlans.vue';
import TaskList from './views/TaskList.vue';
import SystemConfig from './views/SystemConfig.vue';
import HighlightTuning from './views/HighlightTuning.vue';
import OperationLog from './views/OperationLog.vue';
import SecurityAudit from './views/SecurityAudit.vue';
import FeedbackList from './views/FeedbackList.vue';
import { ADMIN_TITLE, APP_ENV } from './config';
import { isAdminLoggedIn, clearAdminToken } from './utils/authStorage';
import { fetchFeedbackUnreadCount } from './api/admin';

const UNREAD_POLL_MS = 30000;

export default {
  name: 'App',
  components: {
    AdminLogin,
    Dashboard,
    TaskList,
    SystemConfig,
    HighlightTuning,
    OperationLog,
    SecurityAudit,
    UserList,
    InviteCodeList,
    QuotaPlans,
    FeedbackList,
  },
  data() {
    return {
      title: ADMIN_TITLE,
      appEnv: APP_ENV,
      active: 'dashboard',
      loggedIn: isAdminLoggedIn(),
      feedbackUnreadCount: 0,
      unreadTimer: null,
    };
  },
  watch: {
    loggedIn(val) {
      if (val) {
        this.startUnreadPolling();
      } else {
        this.stopUnreadPolling();
        this.feedbackUnreadCount = 0;
      }
    },
  },
  mounted() {
    if (this.loggedIn) {
      this.startUnreadPolling();
    }
  },
  beforeDestroy() {
    this.stopUnreadPolling();
  },
  methods: {
    onLoginSuccess() {
      this.loggedIn = true;
    },
    onLogout() {
      clearAdminToken();
      this.loggedIn = false;
    },
    onFeedbackUnreadChange(count) {
      this.feedbackUnreadCount = Number(count) || 0;
    },
    async refreshFeedbackUnread() {
      if (!this.loggedIn) return;
      try {
        const data = await fetchFeedbackUnreadCount();
        this.feedbackUnreadCount = Number(data && data.unreadCount) || 0;
      } catch (e) {
        // 静默：侧栏红点轮询失败不打扰操作
      }
    },
    startUnreadPolling() {
      this.stopUnreadPolling();
      this.refreshFeedbackUnread();
      this.unreadTimer = setInterval(() => {
        this.refreshFeedbackUnread();
      }, UNREAD_POLL_MS);
    },
    stopUnreadPolling() {
      if (this.unreadTimer) {
        clearInterval(this.unreadTimer);
        this.unreadTimer = null;
      }
    },
  },
};
</script>

<style>
body { margin: 0; background: #f3f4f6; }
.header {
  background: #111827;
  color: #fff;
  line-height: 60px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}
.logout-btn { margin-left: auto; color: #d1d5db !important; }
.env-tag { vertical-align: middle; }
.aside { background: #fff; min-height: calc(100vh - 60px); }
.main { padding: 20px; }
.aside .el-menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.nav-badge {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ef4444;
  flex-shrink: 0;
  margin-left: 8px;
}
</style>
